/**
 * Universal unique ID generator utility.
 *
 * Provides a consistent API for generating random unique IDs,
 * abstracting underlying implementations (crypto, uuid, fallback to random+timestamp, etc).
 *
 * ## Features
 * - Uses `crypto.randomUUID` if available and requested (default).
 * - Supports RFC4122-compliant UUID v4 format.
 * - Provides fallback for environments without crypto or uuid.
 * - Optionally allows a prefix/suffix and custom length for random IDs.
 *
 * @param {Object} [options] - Optional configuration object.
 * @param {'uuid'|'simple'} [options.type='uuid'] - Type of ID to generate:
 *   - 'uuid': RFC4122 UUID v4 (36 chars, includes dashes)
 *   - 'simple': Random alphanumeric string (customizable length)
 * @param {number} [options.length=16] - Length of simple ID (ignored for type 'uuid').
 * @param {string} [options.prefix] - Optional prefix to add to the generated ID.
 * @param {string} [options.suffix] - Optional suffix to add to the generated ID.
 * @param {boolean} [options.useCrypto=true] - Use `crypto.randomUUID` if available (for type 'uuid').
 *
 * @returns {string} The generated unique ID.
 *
 * @example
 *   generateId() // RFC4122 UUID (default)
 *   generateId({ type: 'simple', length: 8 }) // e.g., 'kx2jz91a'
 *   generateId({ prefix: 'nx_', suffix: '_tmp' }) // e.g., 'nx_123e4567-e89b-12d3-a456-426614174000_tmp'
 *   generateId({ useCrypto: false }) // fallback, e.g., 'c6d2b5f3-...'
 */
export function generateId(options?: {
  type?: "uuid" | "simple"
  length?: number
  prefix?: string
  suffix?: string
  useCrypto?: boolean
}): string {
  const { type = "uuid", length = 16, prefix = "", suffix = "", useCrypto = true } = options || {}

  let id: string

  if (type === "uuid") {
    // Try crypto.randomUUID if available and requested
    if (useCrypto && typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      id = crypto.randomUUID()
    } else {
      // Fallback to UUID v4 polyfill
      id = uuidv4Polyfill()
    }
  } else {
    // Simple random alphanumeric string
    id = randomString(length)
  }

  return `${prefix || ""}${id}${suffix || ""}`
}

// RFC4122 UUID v4 polyfill (from https://stackoverflow.com/a/2117523/1106414)
function uuidv4Polyfill(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Generates a random alphanumeric string of given length
function randomString(length: number): string {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
  let result = ""
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}
